import {
    GetUserByLoginExtendRequest,
    GetUsersByLoginsRequest,
    SimpleUser,
    UpdateUserRequest,
    User,
    UserType
} from "../../models/user"
import { FileUpload } from "../../models/file"
import { ProjectUser, UpdateProjectUserRequest } from "../../models/projectUser"
import { GlobalUser, UpdateGlobalUserRequest } from "../../models/globalUser"
import { processUser } from "../../utility/common/processUser"
import { PaginationRequest, PaginationResponse } from "../../models/pagination"
import { apiInstance } from "../instances/apiInstance"
import { apiInstanceRemoteWorkplace } from "../instances/apiInstanceRemote"
import { apiInstanceV1 } from "../instances/apiInstanceV1"
import { queryApi } from "../api"
import { globalUsersTag } from "../tags"

export const usersController = {
    update: (request: UpdateUserRequest): Promise<User> =>
        apiInstance
            .put("/users", request)
            .then(response => response.data)
            .then(processUser),
    updateAvatar: (avatar?: FileUpload<Blob>): Promise<User> => {
        const formData = new FormData()
        if (avatar) {
            formData.append("avatar", avatar.Content, avatar.Name)
        }

        return apiInstance
            .put(`user/avatar`, formData)
            .then(response => response.data)
            .then(processUser)
    },
    updateProjectUser: (projectId: string, request: UpdateProjectUserRequest): Promise<ProjectUser> =>
        apiInstance
            .put(`projects/${projectId}/users`, request)
            .then(response => response.data)
            .then(processUser),
    changeProject: (projectId: string): Promise<User> => {
        const config = { headers: { "Content-Type": "application/json" } }
        return apiInstance
            .post(`users/change_project`, `"${projectId}"`, config)
            .then(response => response.data)
            .then(processUser)
    },
    getUsersByLogins: (projectId: string, request: GetUsersByLoginsRequest): Promise<SimpleUser[]> => {
        return apiInstance
            .post(`projects/${projectId}/users/by-logins`, request)
            .then(response => response.data)
            .then(users => users.map(processUser))
    },
    getUserByLoginExtend: (projectId: string, request: GetUserByLoginExtendRequest): Promise<ProjectUser> => {
        return apiInstance
            .post(`projects/${projectId}/users/by-login-extend`, request)
            .then(response => response.data)
            .then(user => processUser(user))
    },
    getCurrentUser: (): Promise<User> => {
        return apiInstance
            .get("users/current")
            .then(response => response.data)
            .then(processUser)
    },
    getUsersByProject: (projectId: string): Promise<ProjectUser[]> =>
        apiInstance
            .get(`projects/${projectId}/users`)
            .then(response => response.data)
            .then(users => users.map(processUser)),
    getPaginatedUsersByProject: (
        projectId: string,
        paginationParams: PaginationRequest
    ): Promise<PaginationResponse<SimpleUser>> =>
        apiInstance
            .post(`${projectId}/users/page`, paginationParams)
            .then(response => response.data)
            .then((response: PaginationResponse<SimpleUser>) => {
                return {
                    ...response,
                    Items: response.Items.map(processUser)
                }
            }),
    getAll: (): Promise<GlobalUser[]> =>
        apiInstance
            .get(`users`)
            .then(response => response.data)
            .then(users => users.map(processUser)),
    getCurrentUserRoleId: (): Promise<string | undefined> => {
        return apiInstance.get("users/current/role_id").then(response => response.data)
    },
    getRemoteWorkplaceAuthToken: (): Promise<string> =>
        apiInstanceRemoteWorkplace.get("get-token").then(response => response.data),
    refreshRemoteWorkplaceAuthToken: (): Promise<string> =>
        apiInstanceRemoteWorkplace.post("refresh-token").then(response => response.data),
    getUserTypes: (customerId: string): Promise<UserType[]> =>
        apiInstanceV1
            .get("users/user_types", {
                params: { customer_id: customerId }
            })
            .then(response => response.data)
}

export const usersApiController = queryApi.injectEndpoints({
    endpoints: build => ({
        getAllUsersByPage: build.query<PaginationResponse<GlobalUser>, PaginationRequest>({
            query: body => ({
                url: `all-users/page?pageFrom=${body.PageFrom}&pageSize=${body.PageSize}&search=${body.Search}`,
                method: "GET"
            }),
            transformResponse: (response: PaginationResponse<GlobalUser>): PaginationResponse<GlobalUser> => ({
                ...response,
                Items: response.Items.map(processUser)
            }),
            providesTags(result) {
                if (!result) return []
                return result.Items.map(user => ({ type: globalUsersTag, id: user.Login }))
            }
        }),

        updateGlobalUser: build.mutation<GlobalUser, UpdateGlobalUserRequest>({
            query: body => ({
                url: `/users/global`,
                method: "PUT",
                body
            }),
            transformResponse: (response: GlobalUser) => processUser(response),
            invalidatesTags(result) {
                const id = result?.Login
                return [{ type: globalUsersTag, id }]
            }
        })
    })
})

export const { useLazyGetAllUsersByPageQuery, useUpdateGlobalUserMutation } = usersApiController
